import { Eye, EyeOff } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Button, Field, Segmented } from '../components/ui';
import { apiUrl, isNativeApp, pingServer, serverBuiltIn, setApiUrl } from '../lib/api';
import { firebaseEnabled, getFirebaseAuth } from '../lib/firebase';
import { useSession } from '../lib/session';
import { LANGS, tr, useLang } from '../lib/i18n';

const demoAllowed = !firebaseEnabled || import.meta.env.DEV;
// Google's sign-in pop-up is blocked inside the Android app's WebView; email/password works everywhere.
const googleAllowed = firebaseEnabled; // web: Google pop-up; Android app: the phone's own Google account picker

export function Login() {
  const signIn = useSession((s) => s.signIn);
  const signInEmployee = useSession((s) => s.signInEmployee);
  const [who, setWho] = useState<'owner' | 'employee'>(() => (localStorage.getItem('sd.lastEmpCode') ? 'employee' : 'owner'));
  const [code, setCode] = useState(() => localStorage.getItem('sd.lastEmpCode') ?? '');
  const [showPwd, setShowPwd] = useState(false);
  const [mode, setMode] = useState<'signin' | 'signup' | 'reset'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(() => { const r = sessionStorage.getItem('sd.signoutReason'); sessionStorage.removeItem('sd.signoutReason'); return r; });

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try { await fn(); } catch (e) { setError(friendly(e)); } finally { setBusy(false); }
  };

  const withGoogle = () => run(async () => {
    const auth = await getFirebaseAuth();
    const { GoogleAuthProvider, signInWithCredential, signInWithPopup } = await import('firebase/auth');
    let cred;
    if (isNativeApp()) {
      // Pop-ups don't run inside the app: pick the account natively, then hand the Google token to Firebase.
      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
      const r = await FirebaseAuthentication.signInWithGoogle();
      if (!r.credential?.idToken) throw new Error('Google sign-in was cancelled.');
      cred = await signInWithCredential(auth, GoogleAuthProvider.credential(r.credential.idToken));
    } else {
      cred = await signInWithPopup(auth, new GoogleAuthProvider());
    }
    await signIn(await cred.user.getIdToken(), 'firebase', cred.user.email ?? undefined);
  });

  const [notice, setNotice] = useState<string | null>(null);
  const forgot = () => run(async () => {
    setNotice(null);
    if (!email) throw new Error('Type your email first, then tap Forgot password.');
    const { sendPasswordResetEmail } = await import('firebase/auth');
    await sendPasswordResetEmail(await getFirebaseAuth(), email);
    setNotice(`We sent a reset link to ${email}.`);
  });

  const withEmail = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const auth = await getFirebaseAuth();
      const m = await import('firebase/auth');
      const cred = mode === 'signin'
        ? await m.signInWithEmailAndPassword(auth, email, password)
        : await m.createUserWithEmailAndPassword(auth, email, password);
      await signIn(await cred.user.getIdToken(), 'firebase', email);
    });
  };

  const employee = (e: FormEvent) => {
    e.preventDefault();
    void run(() => signInEmployee(code, password));
  };

  const demo = (e: FormEvent) => {
    e.preventDefault();
    void run(() => signIn(`dev:${email || 'demo@sensedaily.app'}`, 'demo', email || 'demo@sensedaily.app'));
  };

  const switchWho = (k: 'owner' | 'employee') => { setWho(k); setError(null); setNotice(null); setPassword(''); };
  const title = who === 'employee' ? 'Sign in' : mode === 'signup' ? 'Create your account' : mode === 'reset' ? 'Reset password' : 'Sign in';
  const sub = who === 'employee' ? 'Use the employee code and password your store owner gave you.' : firebaseEnabled ? 'Use your email or Google account.' : 'Demo mode: any email signs in.';

  // Sense Fresh sign-in: logo, title, Owner / Employee switch, then the form in one white card.
  return (
    <div className="flex min-h-app items-center justify-center bg-paper p-5">
      <div className="w-full max-w-md py-10">
        <div className="mb-8 flex items-center gap-2.5"><Logo /><div className="min-w-0 flex-1 leading-tight"><p className="font-bold">{tr('Sense Daily')}</p><p className="text-2xs font-medium text-ink-3">{tr('by SensePOS')}</p></div><LangSwitch /></div>
        <h1 className="text-2xl font-bold">{title}</h1>
        <p className="mt-1 mb-6 text-sm text-ink-3">{sub}</p>
        {mode !== 'reset' && mode !== 'signup' && (
          <div className="mb-4"><Segmented full label={tr('Who is signing in')} value={who} onChange={switchWho} options={[['owner', tr('Owner / email')], ['employee', tr('Employee ID')]]} /></div>
        )}

        <div className="rounded-card border border-line bg-white p-6 shadow-card">
          {who === 'employee' && (
            <form onSubmit={employee} className="flex flex-col gap-4">
              <Field label={tr('Employee code')} required inputMode="numeric" autoComplete="username" placeholder="E.g. 482913-02" value={code} onChange={(e) => setCode(e.target.value)} />
              <PasswordField value={password} onChange={setPassword} show={showPwd} onToggle={() => setShowPwd(!showPwd)} autoComplete="current-password" />
              {error && <p role="alert" className="text-sm text-danger">{error}</p>}
              <Button className="h-12 w-full" disabled={busy}>{busy ? tr('Signing in…') : tr('Sign in')}</Button>
              <p className="text-xs text-ink-3">{tr('Forgot your password? Ask your store owner to reset it in Staff.')}</p>
            </form>
          )}

          {who === 'owner' && firebaseEnabled && (
            <>
              {googleAllowed && mode !== 'reset' && (
                <>
                  <Button variant="secondary" className="h-12 w-full" onClick={withGoogle} disabled={busy}><GoogleMark />{tr('Continue with Google')}</Button>
                  <div className="my-5 flex items-center gap-3 text-2xs tracking-wider text-ink-3 uppercase"><span className="h-px flex-1 bg-line" />{tr('or')}<span className="h-px flex-1 bg-line" /></div>
                </>
              )}
              <form onSubmit={mode === 'reset' ? (e) => { e.preventDefault(); void forgot(); } : withEmail} className="flex flex-col gap-4">
                <Field label={tr('Email')} type="email" autoComplete="email" inputMode="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
                {mode !== 'reset' && <PasswordField value={password} onChange={setPassword} show={showPwd} onToggle={() => setShowPwd(!showPwd)} autoComplete={mode === 'signin' ? 'current-password' : 'new-password'} minLength={6} />}
                {error && <p role="alert" className="text-sm text-danger">{error}</p>}
                {notice && <p role="status" className="text-sm text-success">{notice}</p>}
                <Button className="h-12 w-full" disabled={busy}>{busy ? tr('Please wait…') : mode === 'signup' ? tr('Create account') : mode === 'reset' ? tr('Send reset link') : tr('Sign in')}</Button>
              </form>
            </>
          )}

          {who === 'owner' && demoAllowed && (
            <form onSubmit={demo} className={`flex flex-col gap-4 ${firebaseEnabled ? 'mt-6 border-t border-line pt-6' : ''}`}>
              <Field label={tr('Demo email')} type="email" placeholder="you@store.in" value={email} onChange={(e) => setEmail(e.target.value)} />
              {!firebaseEnabled && error && <p role="alert" className="text-sm text-danger">{error}</p>}
              <Button className="h-12 w-full" variant={firebaseEnabled ? 'secondary' : 'primary'} disabled={busy}>{busy ? tr('Please wait…') : tr('Continue in demo mode')}</Button>
              {!firebaseEnabled && <p className="rounded-xl bg-t-blue p-3 text-xs text-t-blue-fg">{tr('Demo mode: Firebase isn’t configured, so any email signs in. Set the')} <code>{tr('VITE_FIREBASE_*')}</code> {'variables for real sign-in.'}</p>}
            </form>
          )}

          {!serverBuiltIn() && <div className="mt-4"><ServerAddress /></div>}
        </div>

        {who === 'owner' && firebaseEnabled && (
          <div className="mt-6 flex flex-col items-center gap-2 text-sm text-ink-3">
            {mode === 'signin' && <button className="font-semibold text-primary" onClick={() => { setMode('reset'); setError(null); }}>{tr('Forgot password?')}</button>}
            {mode === 'signin'
              ? <p>{tr('New here?')} <button className="font-semibold text-primary" onClick={() => { setMode('signup'); setError(null); }}>{tr('Create an account')}</button></p>
              : <p>{tr('Have an account?')} <button className="font-semibold text-primary" onClick={() => { setMode('signin'); setError(null); setNotice(null); }}>{tr('Sign in')}</button></p>}
          </div>
        )}
      </div>
    </div>
  );
}

function PasswordField({ value, onChange, show, onToggle, autoComplete, minLength }: { value: string; onChange(v: string): void; show: boolean; onToggle(): void; autoComplete: string; minLength?: number }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-muted">{tr('Password')}</span>
      <span className="flex h-11 items-center rounded-xl border border-line bg-white pr-1 focus-within:border-primary focus-within:ring-4 focus-within:ring-primary/10">
        <input type={show ? 'text' : 'password'} required minLength={minLength} autoComplete={autoComplete} value={value} onChange={(e) => onChange(e.target.value)} className="h-full min-w-0 flex-1 rounded-xl bg-transparent px-4 outline-none" />
        <button type="button" aria-label={show ? 'Hide password' : 'Show password'} onClick={onToggle} className="flex h-9 w-9 items-center justify-center rounded-full text-ink-3 hover:bg-surface-2">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
      </span>
    </label>
  );
}

function GoogleMark() {
  return <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>;
}

function Logo() {
  return <img src="/icon.svg" alt="" className="h-9 w-9" />;
}

function friendly(e: unknown): string {
  const msg = (e as { code?: string; message?: string })?.code ?? (e as Error)?.message ?? 'Something went wrong';
  if (msg.includes('invalid-credential') || msg.includes('wrong-password')) return 'Email or password is incorrect.';
  if (msg.includes('email-already-in-use')) return 'An account already exists for this email. Sign in instead.';
  if (msg.includes('popup-closed')) return 'Sign-in window was closed.';
  if (msg.includes('offline')) return 'You are offline. Connect to the internet to sign in the first time.';
  if (msg.includes('wrong_credentials')) return 'Employee code or password is wrong.';
  return msg;
}

/** Which Sense Daily server to use. Required once in the mobile app; optional ("change") on the web. */
function ServerAddress() {
  const [url, setUrl] = useState(apiUrl());
  const [editing, setEditing] = useState(!apiUrl());
  const [state, setState] = useState<'idle' | 'checking' | 'bad'>('idle');
  const save = async (e: FormEvent) => {
    e.preventDefault();
    const v = /^https?:\/\//.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
    setState('checking');
    if (!(await pingServer(v))) return setState('bad');
    setApiUrl(v);
    setUrl(v);
    setState('idle');
    setEditing(false);
  };
  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-xl bg-surface-2 p-3 text-xs text-ink-3">
        <span className="truncate">{tr('Server:')} <span className="num text-ink">{apiUrl().replace(/^https?:\/\//, '')}</span></span>
        <button onClick={() => setEditing(true)} className="shrink-0 font-semibold text-primary">{tr('Change')}</button>
      </div>
    );
  }
  return (
    <form onSubmit={save} className="flex flex-col gap-2 rounded-xl bg-surface-2 p-3">
      <div className="text-xs font-semibold text-ink-2">{tr('Connect to your store server')}</div>
      <div className="text-xs text-ink-3">{tr('The address of your Sense Daily server, e.g.')} <span className="num break-all">{tr('sense-daily.pages.dev')}</span></div>
      <div className="flex gap-2">
        <input value={url} onChange={(e) => { setUrl(e.target.value); setState('idle'); }} placeholder="https://…workers.dev" inputMode="url" autoCapitalize="off" autoCorrect="off" required
          aria-label="Server address" className="h-10 min-w-0 flex-1 rounded-xl border border-line bg-white px-3 text-sm outline-none focus:border-primary" />
        <Button variant="secondary" size="sm" className="h-10" disabled={state === 'checking'}>{state === 'checking' ? tr('Checking…') : tr('Connect')}</Button>
      </div>
      {state === 'bad' && <div className="text-xs text-danger">{tr('Couldn’t reach a Sense Daily server at that address.')}</div>}
    </form>
  );
}

/** English / தமிழ் / हिन्दी before signing in (inside the app it is in the menu and Settings). */
function LangSwitch() {
  const { lang, setLang } = useLang();
  return (
    <div role="radiogroup" aria-label="Language" className="flex shrink-0 gap-0.5 rounded-full bg-surface-2 p-0.5">
      {LANGS.map((l) => (
        <button key={l.id} role="radio" aria-checked={lang === l.id} onClick={() => void setLang(l.id)}
          className={`h-8 rounded-full px-2.5 text-xs font-semibold ${lang === l.id ? 'bg-white text-ink shadow-card' : 'text-ink-3'}`}>{l.id === 'en' ? 'EN' : l.native}</button>
      ))}
    </div>
  );
}
